import React, { useState, useEffect } from "react";
import {
    Table,
    Container,
    Input,
    Row,
    Col,
    Button,
    Modal,
    ModalBody,
    ModalHeader
} from "reactstrap";
import "./Reports.css";
import CreateTableComponent from "../../share/CreateTableComponent";
import { NumberWithCommmas2 } from "../../share/NumberWithCommmas2";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export function Report60Component() {

    /* =========================
       STATE
    ========================== */
    const currentYearTh = new Date().getFullYear() + 543;

    const [startBudgetYear, setStartBudgetYear] = useState(currentYearTh);
    const [endBudgetYear, setEndBudgetYear] = useState(currentYearTh);

    const [reportData, setReportData] = useState([]);
    const [yearOptions, setYearOptions] = useState([]);

    const [detailData, setDetailData] = useState([]);
    const [openModal, setOpenModal] = useState(false);

    const [loading, setLoading] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);

    /* =========================
       PDF FONT
    ========================== */
    function base64FromArrayBuffer(buf) {
        const bytes = new Uint8Array(buf);
        const chunkSize = 0x8000;
        let binary = "";
        for (let i = 0; i < bytes.length; i += chunkSize) {
            binary += String.fromCharCode.apply(
                null,
                bytes.subarray(i, i + chunkSize)
            );
        }
        return btoa(binary);
    }

    let thaiFontLoaded = false;

    async function ensureThaiFontLoaded() {
        if (thaiFontLoaded) return;

        const [regRes, boldRes] = await Promise.all([
            fetch("/fonts/Sarabun-Regular.ttf"),
            fetch("/fonts/Sarabun-Bold.ttf")
        ]);

        const [regBuf, boldBuf] = await Promise.all([
            regRes.arrayBuffer(),
            boldRes.arrayBuffer()
        ]);

        const regB64 = base64FromArrayBuffer(regBuf);
        const boldB64 = base64FromArrayBuffer(boldBuf);

        jsPDF.API.events.push([
            "addFonts",
            function () {
                this.addFileToVFS("Sarabun-Regular.ttf", regB64);
                this.addFileToVFS("Sarabun-Bold.ttf", boldB64);
                this.addFont("Sarabun-Regular.ttf", "Sarabun", "normal");
                this.addFont("Sarabun-Bold.ttf", "Sarabun", "bold");
            }
        ]);

        new jsPDF();
        thaiFontLoaded = true;
    }

    /* =========================
       FETCH SUMMARY
    ========================== */
    async function fetchReport60() {
        try {
            setLoading(true);

            const formData = new FormData();
            formData.append("startBudgetYear", startBudgetYear);
            formData.append("endBudgetYear", endBudgetYear);

            const res = await fetch("/api/SupportReports/dataReport60", {
                method: "POST",
                body: formData
            });

            const json = await res.json();
            setReportData(json.dataenum || []);
        }
        finally {
            setLoading(false);
        }
    }

    /* =========================
       FETCH YEAR OPTIONS
    ========================== */
    async function fetchYearOptions() {
        const res = await fetch("/api/Support/getyear57");
        const json = await res.json();
        setYearOptions(json.dataenum || []);
    }

    /* =========================
       FETCH DETAIL
    ========================== */
    async function fetchDetail(budgetYear) {
        try {
            setDetailLoading(true);
            setOpenModal(true);
            setDetailData([]);

            const res = await fetch(
                "/api/SupportReports/dataReportSupportNames?budgetYear=" + budgetYear
            );
            const json = await res.json();
            setDetailData(json.dataenum || []);
        }
        finally {
            setDetailLoading(false);
        }
    }

    /* =========================
       EFFECT
    ========================== */
    useEffect(() => {
        fetchReport60();
        fetchYearOptions();
    }, [startBudgetYear, endBudgetYear]);

    /* =========================
       DETAIL TABLE COLUMN
    ========================== */
    const DETAIL_COLUMNS = [
        {
            Header: "ลำดับ",
            accessor: (_row, i) => i + 1
        },
        {
            Header: "ชื่อผู้ได้รับเงิน",
            accessor: "supportName"
        },
        {
            Header: "เลขที่คำขอ",
            accessor: "supportNo"
        },
        {
            Header: "จำนวนคน",
            accessor: "countPerson"
        },
        {
            Header: "จำนวนเงิน (บาท)",
            accessor: "paymentAmount"
        }
    ];

    /* =========================
       TOTAL
    ========================== */
    const total = reportData.reduce((acc, cur) => {
        acc.budgetAmount += cur.budgetAmount || 0;
        acc.supportAmount += cur.supportAmount || 0;
        acc.personAmount += cur.personAmount || 0;
        acc.supportPaymentAmount += cur.supportPaymentAmount || 0;
        acc.budgetBalance += cur.budgetBalance || 0;
        return acc;
    }, {
        budgetAmount: 0,
        supportAmount: 0,
        personAmount: 0,
        supportPaymentAmount: 0,
        budgetBalance: 0
    });

    /* =========================
       EXPORT EXCEL
    ========================== */
    function exportExcel() {
        if (!reportData.length) return;

        const data = reportData.map(row => ({
            "ปีงบประมาณ": row.textTitle,
            "งบประมาณ (บาท)": row.budgetAmount,
            "จำนวนแห่ง": row.supportAmount,
            "จำนวนคน": row.personAmount,
            "จ่ายแล้ว (บาท)": row.supportPaymentAmount,
            "ร้อยละใช้": row.perc,
            "งบคงเหลือ": row.budgetBalance,
            "ร้อยละคงเหลือ": row.percBalance
        }));

        data.push({
            "ปีงบประมาณ": "รวม",
            "งบประมาณ (บาท)": total.budgetAmount,
            "จำนวนแห่ง": total.supportAmount,
            "จำนวนคน": total.personAmount,
            "จ่ายแล้ว (บาท)": total.supportPaymentAmount,
            "ร้อยละใช้": "-",
            "งบคงเหลือ": total.budgetBalance,
            "ร้อยละคงเหลือ": "-"
        });

        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Report60");

        XLSX.writeFile(
            workbook,
            `Report60_${startBudgetYear}-${endBudgetYear}.xlsx`
        );
    }

    /* =========================
       EXPORT PDF
    ========================== */
    async function exportPDF() {
        if (!reportData.length) return;

        await ensureThaiFontLoaded();

        const doc = new jsPDF({
            orientation: "landscape",
            unit: "pt",
            format: "a4"
        });

        doc.setFont("Sarabun", "bold");
        doc.setFontSize(14);
        doc.text(
            `สรุปการอนุมัติเงินช่วยเหลือหรืออุดหนุน ปีงบประมาณ ${startBudgetYear} - ${endBudgetYear}`,
            40,
            40
        );

        const body = reportData.map(row => ([
            row.textTitle,
            NumberWithCommmas2(row.budgetAmount),
            row.supportAmount,
            NumberWithCommmas2(row.personAmount),
            NumberWithCommmas2(row.supportPaymentAmount),
            `${row.perc}%`,
            NumberWithCommmas2(row.budgetBalance),
            `${row.percBalance}%`
        ]));

        body.push([
            "รวม",
            NumberWithCommmas2(total.budgetAmount),
            total.supportAmount,
            NumberWithCommmas2(total.personAmount),
            NumberWithCommmas2(total.supportPaymentAmount),
            "-",
            NumberWithCommmas2(total.budgetBalance),
            "-"
        ]);

        doc.setFont("Sarabun", "normal");
        doc.autoTable({
            startY: 60,
            head: [[
                "ปีงบประมาณ",
                "งบประมาณ (บาท)",
                "แห่ง",
                "คน",
                "จ่ายแล้ว (บาท)",
                "ร้อยละ",
                "งบคงเหลือ",
                "ร้อยละ"
            ]],
            body,
            styles: {
                font: "Sarabun",
                fontSize: 10,
                halign: "right"
            },
            headStyles: {
                fontStyle: "bold",
                halign: "center"
            },
            columnStyles: {
                0: { halign: "left" }
            }
        });

        doc.save(`Report60_${startBudgetYear}-${endBudgetYear}.pdf`);
    }

    /* =========================
       RENDER
    ========================== */
    return (
        <Container>

            {/* ===== FILTER ===== */}
            <Row className="mb-3 align-items-center">
                <Col md={2}><h5>ปีงบประมาณ</h5></Col>

                <Col md={3}>
                    <Input
                        type="select"
                        value={startBudgetYear}
                        disabled={loading}
                        onChange={e => setStartBudgetYear(e.target.value)}
                    >
                        {yearOptions.map(y => (
                            <option key={y.yearTh} value={y.yearTh}>
                                {y.yearTh}
                            </option>
                        ))}
                    </Input>
                </Col>

                <Col md={1} className="text-center">ถึง</Col>

                <Col md={3}>
                    <Input
                        type="select"
                        value={endBudgetYear}
                        disabled={loading}
                        onChange={e => setEndBudgetYear(e.target.value)}
                    >
                        {yearOptions
                            .filter(y => y.yearTh >= startBudgetYear)
                            .map(y => (
                                <option key={y.yearTh} value={y.yearTh}>
                                    {y.yearTh}
                                </option>
                            ))}
                    </Input>
                </Col>

                <Col md={3} className="text-end">
                    <Button
                        className="btn-export excel me-2"
                        onClick={exportExcel}
                        disabled={loading || !reportData.length}
                    >
                        Excel
                    </Button>
                    <Button
                        className="btn-export pdf"
                        onClick={exportPDF}
                        disabled={loading || !reportData.length}
                    >
                        PDF
                    </Button>
                </Col>
            </Row>

            {/* ===== TABLE ===== */}
            {loading ? (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" />
                    <div className="mt-2">กำลังโหลดข้อมูล...</div>
                </div>
            ) : (
                <Table bordered hover responsive className="report-table">
                    <thead>
                        <tr>
                            <th rowSpan={2} className="text-center align-middle">ปีงบประมาณ</th>
                            <th rowSpan={2} className="text-center align-middle">จำนวนเงินงบประมาณ (บาท)</th>
                            <th colSpan={3} className="text-center">อนุมัติเงินช่วยเหลือหรืออุดหนุน</th>
                            <th rowSpan={2} className="text-center align-middle">ร้อยละ</th>
                            <th rowSpan={2} className="text-center align-middle">งบประมาณคงเหลือ</th>
                            <th rowSpan={2} className="text-center align-middle">ร้อยละ</th>
                        </tr>
                        <tr>
                            <th className="text-center">(แห่ง)</th>
                            <th className="text-center">(คน)</th>
                            <th className="text-center">(บาท)</th>
                        </tr>
                    </thead>

                    <tbody>
                        {reportData.map((row, i) => (
                            <tr key={i}>
                                <td>{row.textTitle}</td>
                                <td className="text-end">{NumberWithCommmas2(row.budgetAmount)}</td>
                                <td className="text-end">

                                    <span
                                        className=""
                                        style={{ cursor: "pointer" }}
                                        onClick={() => fetchDetail(row.budgetYear)}
                                    >
                                        {row.supportAmount}
                                    </span>
                                </td>
                                <td className="text-end">{NumberWithCommmas2(row.personAmount)}</td>
                                <td className="text-end">{NumberWithCommmas2(row.supportPaymentAmount)}</td>
                                <td className="text-end">{row.perc}%</td>
                                <td className="text-end">{NumberWithCommmas2(row.budgetBalance)}</td>
                                <td className="text-end">{row.percBalance}%</td>
                            </tr>
                        ))}
                    </tbody>

                    <tfoot>
                        <tr className="table-summary">
                            <td className="text-center fw-bold">รวม</td>
                            <td className="text-end fw-bold">{NumberWithCommmas2(total.budgetAmount)}</td>
                            <td className="text-end fw-bold">{NumberWithCommmas2(total.supportAmount)}</td>
                            <td className="text-end fw-bold">{NumberWithCommmas2(total.personAmount)}</td>
                            <td className="text-end fw-bold">{NumberWithCommmas2(total.supportPaymentAmount)}</td>
                            <td className="text-center">–</td>
                            <td className="text-end fw-bold">{NumberWithCommmas2(total.budgetBalance)}</td>
                            <td className="text-center">–</td>
                        </tr>
                    </tfoot>
                </Table>
            )}

            {/* ===== MODAL DETAIL ===== */}
            <Modal
                isOpen={openModal}
                toggle={() => setOpenModal(false)}
                size="xl"
                centered
                backdrop="static"
            >
                <ModalHeader toggle={() => setOpenModal(false)}>
                    รายละเอียดข้อมูล
                </ModalHeader>
                <ModalBody>
                    {detailLoading ? (
                        <div className="text-center py-4">
                            <div className="spinner-border text-primary" />
                            <div className="mt-2">กำลังโหลดรายละเอียด...</div>
                        </div>
                    ) : (
                        <CreateTableComponent
                            column={DETAIL_COLUMNS}
                            dataInput={detailData}
                            showEntrie
                            showPage
                        />
                    )}
                </ModalBody>
            </Modal>

        </Container>
    );
}
